/**
 * PromptInput Component
 * Textarea with attach file button (UI placeholder) and send button
 */

import React, { useCallback, useMemo, type KeyboardEvent } from 'react';
import type { PromptInputProps, AIGeneratorColors } from './AIContentGenerator.types';
import { DEFAULT_COLORS } from './AIContentGenerator.types';
import { useAIGeneratorTranslations } from './i18n';

const DEFAULT_MAX_LENGTH = 5000;

export const PromptInput: React.FC<PromptInputProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  loading = false,
  isRegeneration = false,
  placeholder,
  maxLength = DEFAULT_MAX_LENGTH,
  colors: colorsProp,
  className,
}) => {
  const colors: AIGeneratorColors = useMemo(
    () => ({ ...DEFAULT_COLORS, ...colorsProp }),
    [colorsProp]
  );

  const t = useAIGeneratorTranslations();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !disabled && !loading) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend, disabled, loading]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= maxLength) {
        onChange(newValue);
      }
    },
    [onChange, maxLength]
  );

  const isDisabled = disabled || loading;
  const canSend = value.trim().length > 0 && !isDisabled;
  const charCount = value.length;
  const charCountColor = charCount > maxLength * 0.9 
    ? colors.error 
    : charCount > maxLength * 0.7 
    ? 'var(--color-warning, #f59e0b)' 
    : colors.textMuted;

  return (
    <div
      className={`prompt-input ${className || ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        background: isDisabled ? colors.surface : colors.background,
        overflow: 'hidden',
        transition: 'border-color 200ms',
      }}
    >
      {/* Textarea */}
      <textarea
        className="prompt-input-textarea"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isDisabled}
        rows={4}
        style={{
          width: '100%',
          padding: 16,
          border: 'none',
          outline: 'none',
          resize: 'vertical',
          minHeight: 120,
          maxHeight: 300,
          fontSize: 14,
          lineHeight: 1.6,
          fontFamily: 'inherit',
          background: 'transparent',
          color: isDisabled ? colors.textMuted : colors.text,
        }}
      />

      {/* Footer with buttons */}
      <div
        className="prompt-input-footer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 16,
          borderTop: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        {/* Left side - Attach button (disabled placeholder) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="prompt-input-attach-button"
            disabled
            title="Attach file (coming soon)"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              background: colors.background,
              color: colors.textMuted,
              fontSize: 14,
              cursor: 'not-allowed',
              opacity: 0.6,
              transition: 'all 200ms',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            {t.attachButton}
          </button>
        </div>

        {/* Right side - Character count and Send button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span
            className="prompt-input-char-count"
            style={{
              fontSize: 12,
              color: charCountColor,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {charCount}/{maxLength}
          </span>

          <button
            type="button"
            className="prompt-input-send-button"
            onClick={onSend}
            disabled={!canSend}
            title={canSend ? 'Send (Ctrl+Enter)' : 'Enter a prompt to send'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              border: 'none',
              borderRadius: 6,
              background: canSend ? colors.cta : colors.border,
              color: canSend ? 'var(--color-text-on-cta, #ffffff)' : colors.textMuted,
              fontSize: 14,
              fontWeight: 500,
              cursor: canSend ? 'pointer' : 'not-allowed',
              transition: 'all 200ms',
            }}
            onMouseEnter={(e) => {
              if (canSend) {
                e.currentTarget.style.background = colors.ctaHover || '';
                e.currentTarget.style.filter = colors.ctaHover ? 'none' : 'brightness(0.9)';
              }
            }}
            onMouseLeave={(e) => {
              if (canSend) {
                e.currentTarget.style.background = colors.cta;
                e.currentTarget.style.filter = 'none';
              }
            }}
          >
            {loading ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    animation: 'prompt-spin 1s linear infinite',
                  }}
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="1" />
                </svg>
                {t.generatingButton}
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isRegeneration ? (
                    // Refresh icon for regenerate
                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  ) : (
                    // Send icon for generate
                    <>
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </>
                  )}
                </svg>
                {isRegeneration ? t.regenerateButton : t.generateButton}
              </>
            )}
          </button>
        </div>
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes prompt-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PromptInput;
